// Settings → Connected Devices.
// Wearable sync is NOT live yet. The panel renders a "Coming soon" state plus
// a per-device interest capture. All Junction connect/demo/seed plumbing below
// (handleConnect, handleDemo, handleSeed*, server-fn imports) is preserved
// intact so we can re-enable the active connect UI when wearable sync ships.
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  disconnectWearable,
  listWearableConnections,
  startWearableConnect,
  startWearableDemoConnect,
  seedTestScenario,
  seedComplete90DayPatient,
  seedCoupledValidationData,
} from "@/lib/wearables.functions";
import { supabase } from "@/integrations/supabase/client";
import { getAppleHealthSignInToken } from "@/lib/healthkit.functions";
import { connectAppleHealth, isHealthKitAvailable } from "@/knowmeno/healthkit";
import { SignInForWearablesBanner } from "@/knowmeno/SignInForWearablesBanner";
import { classifyWearableError } from "@/knowmeno/wearableErrors";


import {
  WEARABLE_LABELS,
  WEARABLE_PROVIDERS,
  type WearableProvider,
} from "@/lib/wearables-shared";

type InterestDevice = "oura" | "fitbit" | "apple_watch" | "garmin" | "whoop" | "other";
const INTEREST_DEVICES: { id: InterestDevice; label: string }[] = [
  { id: "oura", label: "Oura" },
  { id: "fitbit", label: "Fitbit" },
  { id: "apple_watch", label: "Apple Watch" },
  { id: "garmin", label: "Garmin" },
  { id: "whoop", label: "Whoop" },
  { id: "other", label: "Other" },
];

function getIsTestUser(): boolean {
  try {
    const raw = window.localStorage.getItem("menotrack_user");
    if (!raw) return false;
    const u = JSON.parse(raw);
    return u.isTestUser === true;
  } catch {
    return false;
  }
}

const PL = "#4B1F4F";
const GR = "#6E4B71";
const GD = "#3a1a3e";
const WH = "#ffffff";
const TL = "#FAF1F4";

interface ConnectionRow {
  provider: WearableProvider;
  status: "pending" | "connected" | "disconnected" | "error";
  connected_at: string | null;
  last_synced_at: string | null;
  last_error: string | null;
}

export function ConnectedDevicesPanel() {
  const list = useServerFn(listWearableConnections);
  const start = useServerFn(startWearableConnect);
  const disconnect = useServerFn(disconnectWearable);
  const demoConnect = useServerFn(startWearableDemoConnect);
  const seedScenario = useServerFn(seedTestScenario);
  const seedFull = useServerFn(seedComplete90DayPatient);
  const seedCoupled = useServerFn(seedCoupledValidationData);
  const mintAppleToken = useServerFn(getAppleHealthSignInToken);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoInfo, setDemoInfo] = useState<string | null>(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedInfo, setSeedInfo] = useState<string | null>(null);
  const [fullBusy, setFullBusy] = useState(false);
  const [fullInfo, setFullInfo] = useState<string | null>(null);
  const [coupledBusy, setCoupledBusy] = useState(false);
  const [coupledInfo, setCoupledInfo] = useState<string | null>(null);
  const [appleAvailable, setAppleAvailable] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  const [appleInfo, setAppleInfo] = useState<string | null>(null);

  useEffect(() => {
    isHealthKitAvailable().then(setAppleAvailable).catch(() => setAppleAvailable(false));
  }, []);

  const handleConnectAppleHealth = async () => {
    setAppleBusy(true);
    setAppleInfo(null);
    setError(null);
    try {
      const { vitalUserId, token, environment, region } = await mintAppleToken();
      const res = await connectAppleHealth({ vitalUserId, vitalToken: token, environment, region });
      if (!res.ok) throw new Error(res.error || "Apple Health connection failed");
      setAppleInfo("Apple Health connected. Your Apple Watch data will start syncing in the background.");
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Couldn't connect Apple Health");
    } finally {
      setAppleBusy(false);
    }
  };


  // Sandbox-only gate: hide outside dev / lovable preview/production hosts.
  const isSandboxHost =
    typeof window !== "undefined" &&
    /(localhost|127\.0\.0\.1|lovable\.app|lovable\.dev)/i.test(window.location.hostname);


  const [rows, setRows] = useState<ConnectionRow[]>([]);
  const [busyProvider, setBusyProvider] = useState<WearableProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authMissing, setAuthMissing] = useState(false);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const { showAuthBanner } = classifyWearableError(undefined);
      if (showAuthBanner) { setRows([]); setAuthMissing(true); return; }
      const res = await list();
      setRows((res?.connections ?? []) as ConnectionRow[]);
      setAuthMissing(false);
    } catch (e: any) {
      const { showAuthBanner, errorMessage } = classifyWearableError(e);
      if (showAuthBanner) {
        setAuthMissing(true);
      } else if (errorMessage) {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    refresh();
  }, []);

  // When user returns from Junction (focus/visibility), re-fetch.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const statusFor = (p: WearableProvider) =>
    rows.find((r) => r.provider === p)?.status ?? "disconnected";

  const handleConnect = async (provider: WearableProvider) => {

    setBusyProvider(provider);
    setError(null);
    const popup = window.open("about:blank", "_blank");
    if (!popup) {
      setBusyProvider(null);
      setError("Couldn't open the connection window. Allow pop-ups and try again.");
      return;
    }
    try {
      const redirectUrl = `${window.location.origin}/app?wearable=${provider}`;
      const { linkUrl } = await start({ data: { provider, redirectUrl } });
      if (!linkUrl || typeof linkUrl !== "string" || !/^https?:\/\//.test(linkUrl)) {
        throw new Error("Junction did not return a valid connection URL.");
      }
      popup.opener = null;
      popup.location.href = linkUrl;
    } catch (e: any) {
      popup.close();
      setError(e?.message || "Couldn't start connection");
    } finally {
      setBusyProvider(null);
    }
  };

  const handleDisconnect = async (provider: WearableProvider) => {
    setBusyProvider(provider);
    setError(null);
    try {
      await disconnect({ data: { provider } });
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Couldn't disconnect");
    } finally {
      setBusyProvider(null);
    }
  };

  const handleDemo = async (provider: "fitbit" | "oura") => {
    setDemoBusy(true);
    setError(null);
    setDemoInfo(null);
    try {
      await demoConnect({ data: { provider } });
      setDemoInfo(
        `Junction is generating 30 days of synthetic ${provider} data. Webhooks usually arrive within ~30s — status will refresh automatically.`,
      );
      // Poll a few times to pick up the connection / first metrics webhooks.
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        await refresh();
      }
    } catch (e: any) {
      setError(e?.message || "Couldn't trigger demo connection");
    } finally {
      setDemoBusy(false);
    }
  };

  const handleSeedScenario = async () => {
    if (!confirm("Seed 14-day test scenario? This will replace existing daily logs, hot-flash events, and wearable metrics for your account.")) return;
    setSeedBusy(true);
    setError(null);
    setSeedInfo(null);
    try {
      const r: any = await seedScenario({});
      setSeedInfo(
        `Seeded ${r.symptomLogRows} symptom-log rows, ${r.hotFlashEvents} hot-flash events, ${r.wearableDays} wearable days. Reload to see the Symptom Intelligence Report unlock.`,
      );
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Couldn't seed test scenario");
    } finally {
      setSeedBusy(false);
    }
  };

  const handleSeedFull = async () => {
    if (!confirm("Seed a COMPLETE 90-day patient? This wipes existing logs, moods, HRT adherence, periods, hot-flash events, lab uploads, and wearable metrics for your account, and rewrites your profile to a perimenopausal test patient on HRT.")) return;
    setFullBusy(true);
    setError(null);
    setFullInfo(null);
    try {
      const r: any = await seedFull({});
      setFullInfo(
        `Seeded ${r.symptomLogRows} symptom-log rows, ${r.moodRows} moods, ${r.hrtAdherenceRows} HRT adherence days, ${r.hotFlashEvents} hot-flash events, ${r.periodRows} period cycles, ${r.labPanels} lab panels, ${r.wearableDays} wearable days. Reload to refresh the app — then regenerate the Symptom Intelligence Report.`,
      );
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Couldn't seed complete patient");
    } finally {
      setFullBusy(false);
    }
  };

  const handleSeedCoupled = async () => {
    if (!confirm("Seed ~90 days of COUPLED validation data? This clears the last 95 days of wearable_metrics and daily_logs for your account and rewrites them with a real but noisy recovery↔symptom relationship. Test accounts only.")) return;
    setCoupledBusy(true);
    setError(null);
    setCoupledInfo(null);
    try {
      const r: any = await seedCoupled({});
      setCoupledInfo(
        `Seeded ${r.totalDays} days (${r.flareDays} flare, ${r.partialDays} partial, ${r.calmDays} calm) — ${r.wearableRows} wearable rows, ${r.symptomRows} symptom rows. Open the Outlook Accuracy panel to validate.`,
      );
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Couldn't seed coupled validation data");
    } finally {
      setCoupledBusy(false);
    }
  };



  const isTestUser = getIsTestUser();

  return (
    <div
      style={{
        background: WH,
        borderRadius: 18,
        padding: 16,
        marginBottom: 14,
        border: "1px solid #F0E0E6",
      }}
    >
      <InterestCapture />
      {appleAvailable ? (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: `1px solid ${TL}`,
            borderRadius: 12,
            background: WH,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: PL, marginBottom: 4 }}>
            Apple Watch
          </div>
          <div style={{ fontSize: 12, color: GR, lineHeight: 1.5, marginBottom: 10 }}>
            Sync sleep, HRV, resting heart rate, and steps from Apple Health.
            We never write to Health — read-only.
          </div>
          {appleInfo ? (
            <div
              style={{
                background: "#E6F4EA",
                color: "#1F5A36",
                borderRadius: 8,
                padding: "6px 8px",
                fontSize: 11,
                marginBottom: 8,
              }}
            >
              {appleInfo}
            </div>
          ) : null}
          <button
            disabled={appleBusy}
            onClick={handleConnectAppleHealth}
            style={{
              background: PL,
              color: WH,
              border: "none",
              borderRadius: 10,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: 500,
              cursor: appleBusy ? "wait" : "pointer",
              opacity: appleBusy ? 0.7 : 1,
            }}
          >
            {appleBusy ? "Connecting…" : "Connect Apple Health"}
          </button>
        </div>
      ) : null}
      {authMissing ? (
        <div style={{ marginTop: 10 }}>
          <SignInForWearablesBanner />
        </div>
      ) : null}
      {error ? (
        <div
          style={{
            background: "#FFE4EA",
            color: "#8A1F1F",
            borderRadius: 10,
            padding: "8px 10px",
            fontSize: 12,
            marginTop: 10,
          }}
        >
          {error}
        </div>
      ) : null}
      {isTestUser && isSandboxHost ? (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            border: "1px dashed #C9A0DC",
            borderRadius: 12,
            background: "#FBF6FF",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 500, color: PL, marginBottom: 6 }}>
            Sandbox testing
          </div>
          <div style={{ fontSize: 11, color: GR, lineHeight: 1.5, marginBottom: 8 }}>
            Load Junction's synthetic 30-day demo data without owning a device.
            Sandbox-only.
          </div>
          {demoInfo ? (
            <div
              style={{
                background: "#E6F4EA",
                color: "#1F5A36",
                borderRadius: 8,
                padding: "6px 8px",
                fontSize: 11,
                marginBottom: 8,
              }}
            >
              {demoInfo}
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              disabled={demoBusy}
              onClick={() => handleDemo("fitbit")}
              style={{
                background: PL,
                color: WH,
                border: "none",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 500,
                cursor: demoBusy ? "wait" : "pointer",
                opacity: demoBusy ? 0.7 : 1,
              }}
            >
              {demoBusy ? "Loading…" : "Load Demo Data (Fitbit)"}
            </button>
            <button
              disabled={demoBusy}
              onClick={() => handleDemo("oura")}
              style={{
                background: "transparent",
                color: PL,
                border: `1px solid ${PL}`,
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 500,
                cursor: demoBusy ? "wait" : "pointer",
                opacity: demoBusy ? 0.7 : 1,
              }}
            >
              {demoBusy ? "…" : "Load Demo Data (Oura)"}
            </button>
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed #C9A0DC" }}>
            {seedInfo ? (
              <div style={{ background: "#E6F4EA", color: "#1F5A36", borderRadius: 8, padding: "6px 8px", fontSize: 11, marginBottom: 8 }}>
                {seedInfo}
              </div>
            ) : null}
            <button
              disabled={seedBusy}
              onClick={handleSeedScenario}
              style={{
                background: "#4B1F4F",
                color: WH,
                border: "none",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 500,
                cursor: seedBusy ? "wait" : "pointer",
                opacity: seedBusy ? 0.7 : 1,
              }}
            >
              {seedBusy ? "Seeding…" : "Seed 14-Day Test Scenario"}

            </button>
            {fullInfo ? (
              <div style={{ background: "#E6F4EA", color: "#1F5A36", borderRadius: 8, padding: "6px 8px", fontSize: 11, marginTop: 10, marginBottom: 8 }}>
                {fullInfo}
              </div>
            ) : null}
            <button
              disabled={fullBusy}
              onClick={handleSeedFull}
              style={{
                background: "#3a1a3e",
                color: WH,
                border: "none",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 500,
                cursor: fullBusy ? "wait" : "pointer",
                opacity: fullBusy ? 0.7 : 1,
                marginTop: 8,
                display: "block",
              }}
            >
              {fullBusy ? "Seeding 90 days…" : "Seed Complete 90-Day Patient"}
            </button>
            {coupledInfo ? (
              <div style={{ background: "#E6F4EA", color: "#1F5A36", borderRadius: 8, padding: "6px 8px", fontSize: 11, marginTop: 10, marginBottom: 8 }}>
                {coupledInfo}
              </div>
            ) : null}
            <button
              disabled={coupledBusy}
              onClick={handleSeedCoupled}
              style={{
                background: "#2a4a6e",
                color: WH,
                border: "none",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 500,
                cursor: coupledBusy ? "wait" : "pointer",
                opacity: coupledBusy ? 0.7 : 1,
                marginTop: 8,
                display: "block",
              }}
              title="Internal validation only — seeds ~90 days with a real but noisy recovery↔symptom coupling so the Outlook Accuracy panel can score against ground truth."
            >
              {coupledBusy ? "Seeding coupled data…" : "Seed coupled validation data"}
            </button>
          </div>

        </div>

      ) : null}
    </div>

  );
}

export default ConnectedDevicesPanel;

function InterestCapture() {
  const [userId, setUserId] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [selected, setSelected] = useState<Set<InterestDevice>>(new Set());
  const [busy, setBusy] = useState<InterestDevice | null>(null);
  const [justAdded, setJustAdded] = useState<InterestDevice | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id ?? null;
      if (cancelled) return;
      setUserId(uid);
      setAuthChecked(true);
      if (!uid) return;
      const { data: rows } = await supabase
        .from("wearable_interest")
        .select("device")
        .eq("user_id", uid);
      if (cancelled) return;
      if (rows) {
        setSelected(new Set(rows.map((r: { device: string }) => r.device as InterestDevice)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pick = async (device: InterestDevice) => {
    if (!userId) return;
    if (selected.has(device)) return;
    setBusy(device);
    setErr(null);
    try {
      const { error } = await supabase
        .from("wearable_interest")
        .upsert({ user_id: userId, device }, { onConflict: "user_id,device", ignoreDuplicates: true });
      if (error) throw error;
      setSelected((s) => new Set(s).add(device));
      setJustAdded(device);
      try {
        const ph = (window as unknown as { posthog?: { capture: (e: string, p?: Record<string, unknown>) => void } }).posthog;
        ph?.capture("wearable_interest_expressed", { device });
      } catch {
        // PostHog not initialized — fine.
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Couldn't save your interest. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: GD, marginBottom: 6 }}>
        Wearable sync — coming soon
      </div>
      <div style={{ fontSize: 13, color: GR, lineHeight: 1.5, marginBottom: 12 }}>
        Soon you'll connect your Oura or Fitbit and let KnowMeno read your sleep,
        heart-rate variability, resting heart rate, and overnight temperature
        automatically — no extra logging — and fold it straight into your symptom
        trends.
      </div>
      {authChecked && !userId ? (
        <div style={{ fontSize: 12, color: GR, lineHeight: 1.5 }}>
          Sign in to tell us which device you'd connect.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: GD, marginBottom: 8 }}>
            Want this? Tell us which device you'd connect:
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {INTEREST_DEVICES.map((d) => {
              const isSel = selected.has(d.id);
              const isBusy = busy === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  disabled={isBusy || isSel || !userId}
                  onClick={() => pick(d.id)}
                  style={{
                    background: isSel ? PL : WH,
                    color: isSel ? WH : PL,
                    border: `1px solid ${PL}`,
                    borderRadius: 999,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: isSel ? "default" : isBusy ? "wait" : "pointer",
                    opacity: isBusy ? 0.7 : 1,
                  }}
                >
                  {isSel ? `✓ ${d.label}` : d.label}
                </button>
              );
            })}
          </div>
          {justAdded ? (
            <div style={{ fontSize: 12, color: "#2F7A4E", marginTop: 8 }}>
              Thanks — we'll let you know when it's ready.
            </div>
          ) : null}
          {err ? (
            <div style={{ fontSize: 12, color: "#8A1F1F", marginTop: 8 }}>{err}</div>
          ) : null}
        </>
      )}
    </div>
  );
}
